// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseInput,
  BaseSelect,
  BaseTimePicker,
  ConfirmModal,
  Loading,
} from '~/components/common';
// Others
import { IFormSchedule, IScheduleDetail } from '~/utils/interface/schedule';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { defaultValuesForm, schema } from './helper';
import { RoleEnum, ScheduleTypeEnum, TimeFormatEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getJobList } from '~/thunks/job/jobThunk';
import { IJob } from '~/utils/interface/job';
import { getContactList } from '~/thunks/contact/contactThunk';
import { IContact } from '~/utils/interface/contact';
import { compareDataUpdate, dataTimeZone, formatTime, getFullName } from '~/utils/helper/common';
import { ICustomer } from '~/utils/interface/customer';
import { getCustomerList } from '~/thunks/customer/customerThunk';
import { IEmployee } from '~/utils/interface/employee';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import {
  createSchedule,
  deleteSchedule,
  getScheduleDetail,
  updateSchedule,
} from '~/thunks/schedule/scheduleThunk';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { jobActions } from '~/thunks/job/jobSlice';
import { customerActions } from '~/thunks/customer/customerSlice';
import useDebounce from '~/utils/hooks/useDebounce';
// Styles, images, icons
import styles from './FormSchedule.module.scss';
import { icons } from '~/assets';

type Props = {
  scheduleId?: string;
  jobId?: string;
  customerId?: string;
  estimateId?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormSchedule = (props: Props) => {
  //#region Destructuring Props
  const { scheduleId, jobId, customerId, estimateId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormSchedule>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const scheduleType = watch('businessType');
  const timezone = watch('timezone');
  const { id: jobIdCurrent } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [jobList, setJobList] = useState<IJob[]>([]);
  const [contactList, setContactList] = useState<IContact[]>([]);
  const [customerList, setCustomerList] = useState<ICustomer[]>([]);
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([]);
  const [initialData, setInitialData] = useState<IFormSchedule>(defaultValuesForm);

  const [isLoadingJob, setIsLoadingJob] = useState<boolean>(false);
  const [isLoadingContact, setIsLoadingContact] = useState<boolean>(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState<boolean>(false);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);

  const [jobSearchKey, setJobSearchKey] = useState<string>(EMPTY_STRING);
  const [contactSearchKey, setContactSearchKey] = useState<string>(EMPTY_STRING);
  const [customerSearchKey, setCustomerSearchKey] = useState<string>(EMPTY_STRING);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceJobSearchKey = useDebounce(jobSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const debounceContactSearchKey = useDebounce(contactSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const debounceCustomerSearchKey = useDebounce(customerSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    setValue('businessId', jobId, { shouldValidate: true });
    setValue('businessType', ScheduleTypeEnum.BOOKING_A_JOB, { shouldValidate: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (!customerId) return;

    setValue('businessId', customerId, { shouldValidate: true });
    setValue('businessType', ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER, { shouldValidate: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!estimateId) return;

    setValue('businessId', estimateId, { shouldValidate: true });
    setValue('businessType', ScheduleTypeEnum.ESTIMATE, { shouldValidate: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceJobSearchKey && { textSearch: debounceJobSearchKey }),
      ...(debounceContactSearchKey && { textSearch: debounceContactSearchKey }),
      ...(debounceCustomerSearchKey && { textSearch: debounceCustomerSearchKey }),
    };

    if (scheduleType === ScheduleTypeEnum.BOOKING_A_JOB) {
      handleGetJobList(params);
    }

    if (scheduleType === ScheduleTypeEnum.BOOKING_A_CALL) {
      handleGetContactList(params);
    }

    if (scheduleType === ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER) {
      handleGetCustomerList(params);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleType, debounceJobSearchKey, debounceContactSearchKey, debounceCustomerSearchKey]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetEmployeeList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceEmployeeSearchKey]);

  useEffect(() => {
    if (!scheduleId) return;

    handleGetScheduleDetail(scheduleId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetScheduleDetail = (scheduleId: string) => {
    setIsLoading(true);

    dispatch(getScheduleDetail(scheduleId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const scheduleDetail: IScheduleDetail = res?.data;

        const dataForm: IFormSchedule = {
          businessType: scheduleDetail?.businessType,
          businessId: scheduleDetail?.event?.id || EMPTY_STRING,
          startDate: scheduleDetail?.startDate || EMPTY_STRING,
          endDate: scheduleDetail?.endDate || EMPTY_STRING,
          startTime: scheduleDetail?.startTime
            ? formatTime(scheduleDetail?.startTime, TimeFormatEnum.HH_MM)
            : EMPTY_STRING,
          endTime: scheduleDetail?.startTime
            ? formatTime(scheduleDetail?.endTime, TimeFormatEnum.HH_MM)
            : EMPTY_STRING,
          assigneeIds: scheduleDetail?.assignees?.map((schedule) => schedule.id),
          eventName: scheduleDetail?.event?.name || EMPTY_STRING,
          note:
            scheduleDetail?.businessType === ScheduleTypeEnum.EVENT
              ? scheduleDetail?.event?.note || EMPTY_STRING
              : scheduleDetail?.note || EMPTY_STRING,
          timezone: dataTimeZone() || EMPTY_STRING,
        };

        setInitialData(dataForm);
        reset(dataForm);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetJobList = (params: IQueryParamBase) => {
    setIsLoadingJob(true);

    dispatch(getJobList(params))
      .unwrap()
      .then((resp) => {
        const { responses } = resp.data;
        if (!responses) return;

        // setJobList((prev) => {
        //   const newData = responses?.filter((data) => !prev.some((oldData) => oldData.id === data.id));

        //   return [...prev, ...newData];
        // });

        setJobList(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingJob(false);
      });
  };

  const handleGetContactList = (params: IQueryParamBase) => {
    setIsLoadingContact(true);

    dispatch(getContactList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        // setContactList((prev) => {
        //   const newData = responses?.filter((data) => !prev.some((oldData) => oldData.id === data.id));

        //   return [...prev, ...newData];
        // });

        setContactList(responses);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoadingContact(false);
      });
  };

  const handleGetCustomerList = (params: IQueryParamBase) => {
    setIsLoadingCustomer(true);

    dispatch(getCustomerList(params))
      .unwrap()
      .then((resp) => {
        const { responses } = resp.data;
        if (!responses) return;

        // setCustomerList((prev) => {
        //   const newData = responses?.filter((data) => !prev.some((oldData) => oldData.id === data.id));

        //   return [...prev, ...newData];
        // });

        setCustomerList(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingCustomer(false);
      });
  };

  const handleGetEmployeeList = (params: IQueryParamBase) => {
    setIsLoadingEmployee(true);

    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        const dataFiltered = responses?.filter((employee) => employee.role !== RoleEnum.ADMIN);

        if (!responses) return;

        // if (Array.isArray(responses)) {
        //   setEmployeeList((prev) => {
        //     const newData = dataFiltered?.filter((data) => !prev.some((oldData) => oldData.id === data.id));

        //     return [...prev, ...newData];
        //   });
        // }

        setEmployeeList(dataFiltered);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };

  const handleCancelFormSchedule = () => {
    onClose();
    reset();
  };

  const handleDeleteSchedule = () => {
    setIsOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!scheduleId) return;

    setIsLoading(true);

    dispatch(deleteSchedule(scheduleId))
      .unwrap()
      .then((res) => {
        handleCancelFormSchedule();
        handleResetListSchedule();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleSubmitFormSchedule = (data: IFormSchedule) => {
    if (scheduleId) {
      handleEditSchedule(scheduleId, data);
    } else {
      handleAddSchedule(data);
    }
  };

  const handleAddSchedule = (data: IFormSchedule) => {
    let bodySchedule: IFormSchedule = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== EMPTY_STRING)
    ) as IFormSchedule;

    if (jobIdCurrent) {
      bodySchedule = {
        ...bodySchedule,
        jobId: jobIdCurrent,
      };
    }

    setIsLoading(true);

    dispatch(createSchedule(bodySchedule))
      .unwrap()
      .then((res) => {
        handleResetListSchedule();
        handleCancelFormSchedule();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetListSchedule = () => {
    dispatch(scheduleActions.setRefreshScheduleList(true));

    if (jobId || jobIdCurrent) {
      dispatch(jobActions.setIsRefreshScheduleData(true));
      return;
    }

    if (customerId) {
      dispatch(customerActions.setIsRefreshScheduleData(true));
      return;
    }

    dispatch(scheduleActions.changeNeedRefreshData(true));
  };

  const handleEditSchedule = (scheduleId: string, body: IFormSchedule) => {
    const dataChange: IFormSchedule = compareDataUpdate(initialData, body) as IFormSchedule;

    const { eventName, businessId, ...rest } = dataChange;

    // compareDataUpdate does a positional array diff and returns only newly added items,
    // not the full selection. For assigneeIds the backend expects the complete replacement
    // set, so always forward the full current selection from the form.
    const payloadRest = { ...rest, assigneeIds: body.assigneeIds };

    setIsLoading(true);

    dispatch(
      updateSchedule({
        scheduleId,
        body: scheduleType === ScheduleTypeEnum.EVENT ? { eventName, ...payloadRest } : { businessId, ...payloadRest },
      })
    )
      .unwrap()
      .then((res) => {
        handleCancelFormSchedule();
        handleResetListSchedule();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchJobChange = (searchKey: string) => {
    setJobSearchKey(searchKey);
  };

  const handleSearchContactChange = (searchKey: string) => {
    setContactSearchKey(searchKey);
  };

  const handleSearchCustomerChange = (searchKey: string) => {
    setCustomerSearchKey(searchKey);
  };

  const handleSearchEmployeeChange = (searchKey: string) => {
    setEmployeeSearchKey(searchKey);
  };

  //#endregion Handle Function

  return (
    <Dialog id='formSchedulePage' open={true} fullWidth maxWidth={'md'} onClose={handleCancelFormSchedule}>
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitFormSchedule)} className={cx('form')}>
        <DialogTitle>{t(scheduleId ? 'form_schedule_title_edit' : 'form_schedule_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              {/* <Grid2 size={6}>
                <Controller
                  name='businessType'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_schedule_label_type')}
                      value={value}
                      options={mockDataScheduleType || []}
                      onChange={({ value: optionValue }: IBaseOption) => {
                        onChange(optionValue);
                        setValue('businessId', EMPTY_STRING);
                      }}
                      required
                      errorMessage={errors.businessType?.message}
                    />
                  )}
                />
              </Grid2> */}

              <Grid2 size={6}>
                {scheduleType && scheduleType === ScheduleTypeEnum.BOOKING_A_JOB && (
                  <Controller
                    name='businessId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseSelect
                        label={t('form_schedule_label_job')}
                        value={value}
                        options={jobList?.map((job) => ({ label: job.name, value: job.id })) || []}
                        onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                        onSearchChange={handleSearchJobChange}
                        isLoading={isLoadingJob}
                        required
                        errorMessage={errors.businessId?.message}
                      />
                    )}
                  />
                )}

                {scheduleType && scheduleType === ScheduleTypeEnum.BOOKING_A_CALL && (
                  <Controller
                    name='businessId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseSelect
                        label={t('form_schedule_label_contact')}
                        value={value}
                        options={
                          contactList?.map((contact) => ({
                            label: getFullName({ ...contact }),
                            value: contact.id,
                          })) || []
                        }
                        onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                        onSearchChange={handleSearchContactChange}
                        isLoading={isLoadingContact}
                        required
                        errorMessage={errors.businessId?.message}
                      />
                    )}
                  />
                )}

                {scheduleType && scheduleType === ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER && (
                  <Controller
                    name='businessId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseSelect
                        label={t('form_schedule_label_return_customer')}
                        value={value}
                        options={
                          customerList?.map((customer) => ({
                            label: getFullName({ ...customer }),
                            value: customer.id,
                          })) || []
                        }
                        onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                        onSearchChange={handleSearchCustomerChange}
                        isLoading={isLoadingCustomer}
                        required
                        errorMessage={errors.businessId?.message}
                      />
                    )}
                  />
                )}

                {scheduleType && scheduleType === ScheduleTypeEnum.EVENT && (
                  <Controller
                    name='eventName'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        required
                        label={t('form_schedule_label_event_name')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.eventName?.message}
                      />
                    )}
                  />
                )}
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='assigneeIds'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      required
                      multiple={true}
                      label={t('form_schedule_label_assignees')}
                      value={value}
                      onChange={onChange}
                      options={
                        employeeList?.map((employee) => ({
                          label: getFullName({ ...employee }),
                          value: employee.id,
                        })) || []
                      }
                      onSearchChange={handleSearchEmployeeChange}
                      isLoading={isLoadingEmployee}
                      errorMessage={errors?.assigneeIds?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            {scheduleType && (
              <>
                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'} alignItems='flex-end'>
                  <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'} alignItems='flex-end'>
                    <Grid2 size={3.5}>
                      <Controller
                        name='startDate'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseDatePicker
                            label={t('form_schedule_label_from')}
                            value={value}
                            onChange={onChange}
                            required
                            errorMessage={errors?.startDate?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={2.5}>
                      <Controller
                        name='startTime'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseTimePicker
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.startTime?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={3.5}>
                      <Controller
                        name='endDate'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseDatePicker
                            value={value}
                            label={t('form_schedule_label_to')}
                            onChange={onChange}
                            required
                            errorMessage={errors?.endDate?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={2.5}>
                      <Controller
                        name='endTime'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseTimePicker
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.endTime?.message}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>

                  <Grid2 size={12}>
                    <Typography component={'p'} className={cx('timezone')}>
                      {t('form_schedule_label_time_zone', {
                        data: timezone || EMPTY_STRING,
                      })}
                    </Typography>
                  </Grid2>
                </Grid2>

                <Grid2 size={12}>
                  <Controller
                    name='note'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        multiline
                        height={'auto'}
                        rows={6}
                        label={t('form_schedule_label_note')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.note?.message}
                      />
                    )}
                  />
                </Grid2>

                {/* <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  <Grid2 size={6}>
                    <Controller
                      name='assigneeIds'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseSelect
                          required
                          multiple={true}
                          label={t('form_schedule_label_assignees')}
                          value={value}
                          onChange={onChange}
                          options={
                            employeeList?.map((employee) => ({
                              label: getFullName({ ...employee }),
                              value: employee.id,
                            })) || []
                          }
                          onSearchChange={handleSearchEmployeeChange}
                          isLoading={isLoadingEmployee}
                          errorMessage={errors?.assigneeIds?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2> */}
              </>
            )}

            {/* {scheduleType && scheduleType === ScheduleTypeEnum.EVENT && !isLoading && (
              <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  <Grid2 size={4}>
                    <Controller
                      name='address'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInputAddress
                          label={t('form_schedule_label_address')}
                          value={value}
                          onChange={onChange}
                          onSelectAddress={handleSelectAddress}
                          errorMessage={errors?.address?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='address2'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInputAddress
                          label={t('form_schedule_label_second_address')}
                          value={value}
                          onChange={onChange}
                          onSelectAddress={handleSelectAddress}
                          errorMessage={errors?.address2?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='city'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_schedule_label_city')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors.city?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  <Grid2 size={4}>
                    <Controller
                      name='state'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_schedule_label_state')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors?.state?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='zipCode'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_schedule_label_zip_code')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors?.zipCode?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='country'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_schedule_label_country')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors?.country?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            )} */}
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            {scheduleId && (
              <Button variant='outlined' onClick={handleDeleteSchedule} className={cx('buttonDelete')}>
                {t('common_action_delete', { ns: 'common/base' })}
              </Button>
            )}

            <Button variant='outlined' onClick={handleCancelFormSchedule}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={scheduleId ? !isDirty : false}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </DialogActions>

        {isOpenConfirm && (
          <ConfirmModal
            type='delete'
            icon={icons.tableIconActionDelete}
            title={t('common_title_confirm_delete', { ns: 'common/base' })}
            textConfirm={t('common_action_delete', { ns: 'common/base' })}
            onClose={handleCloseConfirmDelete}
            onConfirm={handleConfirmDelete}
          />
        )}

        {isLoading && <Loading />}
      </Box>
    </Dialog>
  );
};

export default FormSchedule;
